import * as React from "react";

import {
  InputSuffix,
  TextBox,
  TextBoxChangeEvent,
  TextBoxHandle,
  TextBoxProps,
} from "@progress/kendo-react-inputs";

import { FloatingLabel } from "@progress/kendo-react-labels";
import { StackLayout } from "@progress/kendo-react-layout";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { CPText } from "shared/components/base";
import CPFontAwesome from "../CPFontAwesome/CPFontAwesome";
import "./CPTextBoxStyles.scss";
import {
  RegExpPatterns,
  filterValidCharacters,
  getFieldError,
} from "shared/helpers";

export interface CPTextBoxProps extends Omit<
  TextBoxProps,
  "name" | "suffix" | "prefix" | "label" | "required" | "onChange" | "onBlur"
> {
  /** Unique field name; used as the `name` attribute and included in all change/blur events. */
  name: string;
  /** Floating label displayed above the input. */
  label?: string;
  /** Helper text shown below the input when there is no error. */
  helpText?: string;
  /** Error message displayed below the input in red; replaces `helpText` when set. */
  errorMsg?: string;
  /** Appends ` *` to the label to indicate the field is required. */
  required?: boolean;
  /** Shows a small spinner in the suffix area while `true`. */
  showLoader?: boolean;
  /** Strips space characters from the input value as the user types. */
  disableSpaces?: boolean;
  /** Restricts input to letters only (a-z, A-Z). Combine with `disableSpaces` to also block spaces. */
  textOnly?: boolean;
  /** Restricts input to alphanumeric characters. Combine with `disableSpaces` to also block spaces. */
  alphaNumeric?: boolean;
  /** Allows letters, spaces, hyphens, and apostrophes — suitable for personal names. */
  personNameCharacters?: boolean;
  /** Transforms entered text to uppercase on every keystroke. */
  toUppercase?: boolean;
  /** Capitalises the first letter on every keystroke. */
  toCapital?: boolean;
  /** Custom `RegExp` used to filter input characters. Takes precedence over the preset filter props. */
  regexPattern?: RegExp;
  /** Render function for a custom suffix element (e.g. an icon or button) placed inside the input. */
  renderSuffix?: () => JSX.Element;
  /** Render function for a custom prefix element placed inside the input. */
  renderPrefix?: () => JSX.Element;
  /** Raw API error response `{ errors: [{ propertyName, errorMessage }] }`. Used with `name` to resolve a field-level error. */
  error?: any;
  /** Callback fired on every value change. Receives a normalised `CPTextBoxChangeEvent`. */
  onChange?: (event: CPTextBoxChangeEvent) => void;
  /** Callback fired when the input loses focus. Receives a normalised `CPTextBoxEvent`. */
  onBlur?: (event: CPTextBoxEvent) => void;
  /** When provided, the help icon becomes a clickable button that fires this callback instead of showing a tooltip. */
  onHelpClick?: () => void;
  ref?: React.MutableRefObject<any>;
}

export interface CPTextBoxChangeEvent extends Omit<CPTextBoxEvent, "type"> {
  type: "CPTextBoxChangeEvent";
}

export interface CPTextBoxEvent {
  name: string;
  type: "CPTextBoxEvent";
  syntheticEvent: React.SyntheticEvent<HTMLElement, Event>;
  value: React.InputHTMLAttributes<HTMLInputElement>["value"];
  error: string | undefined;
  isValid: boolean;
  target: TextBoxHandle;
}

const defaultErrorMsg = "This field is required.";

/**
 * Wrapper around the Kendo `TextBox` with floating label, character filtering, validation display,
 * and normalised change/blur events that include the field `name`.
 *
 * @example
 * ```tsx
 * // Basic controlled input
 * <CPTextBox name="firstName" label="First Name" value={form.firstName} onChange={handleChange} />
 *
 * // Letters-only, capitalised, with help text
 * <CPTextBox
 *   name="city"
 *   label="City"
 *   textOnly
 *   toCapital
 *   helpText="Enter the city name"
 *   value={form.city}
 *   onChange={handleChange}
 * />
 *
 * // Required with error message
 * <CPTextBox
 *   name="username"
 *   label="Username"
 *   required
 *   value={form.username}
 *   errorMsg={errors.username}
 *   onChange={handleChange}
 * />
 * ```
 */
const CPTextBox: React.FC<CPTextBoxProps> = (props) => {
  const [dirty, setDirty] = React.useState(false);

  React.useEffect(() => {
    if (props.error != null) setDirty(false);
  }, [props.error]);

  const formatString = React.useCallback(
    (value: string | undefined) => {
      if (value == null || value.length === 0) return "";

      if (props.regexPattern) {
        return filterValidCharacters(value, props.regexPattern);
      }

      if (props.textOnly) {
        return filterValidCharacters(
          value,
          props.disableSpaces
            ? RegExpPatterns.lettersOnly
            : RegExpPatterns.lettersSpaces,
        );
      }

      if (props.alphaNumeric) {
        return filterValidCharacters(
          value,
          props.disableSpaces
            ? RegExpPatterns.alphaNumeric
            : RegExpPatterns.alphaNumericSpaces,
        );
      }

      if (props.personNameCharacters) {
        return filterValidCharacters(
          value,
          RegExpPatterns.personNameCharacters,
        );
      }

      if (props.disableSpaces) {
        // use alphanumeric no spaces for now...
        return filterValidCharacters(value, RegExpPatterns.alphaNumeric);
      }

      return value;
    },
    [props],
  );

  const handleChange = (event: TextBoxChangeEvent) => {
    if (props.onChange) {
      setDirty(true);
      const isValid = event.target.value != null || event.target.value !== "";
      let value = formatString(event.value as string);

      if (props.toUppercase) {
        value = value.toUpperCase();
      }

      props.onChange({
        name: props.name ?? "",
        type: "CPTextBoxChangeEvent",
        syntheticEvent: event.syntheticEvent,
        target: event.target,
        value: value,
        isValid: isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const handleBlur = (event: React.FocusEvent<any, Element>) => {
    if (props.onBlur) {
      const isValid = event.target.value != null || event.target.value !== "";
      let value = event.target.value ?? props.value ?? "";

      if (props.toUppercase) {
        value = value.toUpperCase();
      }

      props.onBlur({
        name: props.name ?? "",
        type: "CPTextBoxEvent",
        syntheticEvent: event,
        target: event.target,
        value: value,
        isValid: isValid,
        error: isValid ? undefined : defaultErrorMsg,
      });
    }
  };

  const renderLoader = () => {
    return (
      <InputSuffix>
        <CPFontAwesome icon="fa-solid fa-circle-notch fa-spin" />
      </InputSuffix>
    );
  };

  const helpIconStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 16,
    height: 16,
    borderRadius: "50%",
    border: "1px solid #0066cc",
    fontSize: 10,
    fontWeight: "bold",
    color: "#0066cc",
    flexShrink: 0,
  };

  const renderHelpIcon = () => (
    <InputSuffix>
      {props.onHelpClick ? (
        <button
          type="button"
          onClick={props.onHelpClick}
          aria-label="Field help"
          style={{
            ...helpIconStyle,
            cursor: "pointer",
            background: "none",
            padding: 0,
          }}
        >
          ?
        </button>
      ) : (
        <Tooltip
          anchorElement="target"
          position="top"
          className="cp-help-tooltip"
          tooltipStyle={{
            backgroundColor: "#fff",
            color: "#222",
            border: "1px solid #ccc",
            borderRadius: "6px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            maxWidth: 280,
            fontSize: 13,
          }}
        >
          <span
            title={props.helpText}
            style={{ ...helpIconStyle, cursor: "help", userSelect: "none" }}
          >
            ?
          </span>
        </Tooltip>
      )}
    </InputSuffix>
  );

  const renderSuffix = () => {
    const helpIcon =
      props.helpText || props.onHelpClick ? renderHelpIcon() : null;

    if (props.renderSuffix) {
      return helpIcon ? (
        <>
          {props.renderSuffix()}
          {helpIcon}
        </>
      ) : (
        props.renderSuffix()
      );
    }

    if (props.showLoader) {
      return helpIcon ? (
        <>
          {renderLoader()}
          {helpIcon}
        </>
      ) : (
        renderLoader()
      );
    }

    return helpIcon;
  };

  const renderTextBox = () => {
    return (
      <TextBox
        ref={(input) => {
          if (input?.element && props.toCapital) {
            input.element.setAttribute("style", "text-transform:capitalize");
          }
        }}
        style={{
          ...props.style,
        }}
        onChange={handleChange}
        onBlur={handleBlur}
        className={props.className}
        color={props.color}
        onFocus={props.onFocus}
        id={props.name}
        placeholder={props.placeholder}
        size={props.size ?? "small"}
        fillMode={props.fillMode}
        rounded={props.rounded}
        name={props.name}
        autoComplete="off"
        suffix={renderSuffix}
        prefix={props.renderPrefix}
        type={props.type ?? "text"}
        value={props.value ?? ""}
        maxLength={props.maxLength}
        disabled={props.disabled}
      />
    );
  };

  const resolvedError =
    props.errorMsg ??
    getFieldError(dirty ? undefined : props.error, props.name);

  return (
    <StackLayout orientation="vertical" gap={4}>
      {props.label != null ? (
        <FloatingLabel
          label={props.required ? `${props.label} *` : props.label}
          editorValue={props.value as string}
          editorId={props.name}
        >
          {renderTextBox()}
        </FloatingLabel>
      ) : (
        renderTextBox()
      )}
      {resolvedError != null ? (
        <CPText textStyle="caption" color="error" style={{ opacity: 0.8 }}>
          {resolvedError}
        </CPText>
      ) : null}
    </StackLayout>
  );
};

export default CPTextBox;
